import { useEffect, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Card,
  CardContent,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
  Skeleton,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

// ** Hooks
import {
  useGetSettingsQuery,
  useUpdateSettingsBatchMutation,
} from "@/api/settingsApi.ts";

// ** Utils
import { mutationFeedback } from "@/services/utils.tsx";

// ** Schema
import {
  settingsSchema,
  settingsDefaultValues,
  type SettingsFormType,
} from "@/schemas/settings.ts";

// ** Types
import type { components } from "@/types/api/generated-api-schema.ts";

type Setting = components["schemas"]["Setting.jsonld-setting.read_read"];

// Setting keys mapping
const SETTING_KEYS = {
  residence_name: "residence_name",
  address_line1: "address_line1",
  address_line2: "address_line2",
  phone: "phone",
  email: "email",
  email_notification: "email_notification",
  opening_hours: "opening_hours",
  email_notifications_enabled: "email_notifications_enabled",
} as const;

export const Route = createFileRoute("/admin/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  const { t } = useTranslation();

  // API
  const { data, isLoading, error } = useGetSettingsQuery();
  const [updateSettings, { isLoading: isUpdating }] =
    useUpdateSettingsBatchMutation();

  // Build a map of settings by key
  const settingsMap = useMemo(() => {
    const map: Record<string, Setting> = {};
    if (data?.member) {
      for (const setting of data.member) {
        if (setting.key) {
          map[setting.key] = setting;
        }
      }
    }
    return map;
  }, [data]);

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<SettingsFormType>({
    resolver: zodResolver(settingsSchema),
    defaultValues: settingsDefaultValues,
  });

  // Populate form with API data
  useEffect(() => {
    if (data?.member) {
      const formValues: SettingsFormType = {
        residence_name: settingsMap[SETTING_KEYS.residence_name]?.value ?? "",
        address_line1: settingsMap[SETTING_KEYS.address_line1]?.value ?? "",
        address_line2: settingsMap[SETTING_KEYS.address_line2]?.value ?? "",
        phone: settingsMap[SETTING_KEYS.phone]?.value ?? "",
        email: settingsMap[SETTING_KEYS.email]?.value ?? "",
        email_notification:
          settingsMap[SETTING_KEYS.email_notification]?.value ?? "",
        opening_hours: settingsMap[SETTING_KEYS.opening_hours]?.value ?? "",
        email_notifications_enabled:
          settingsMap[SETTING_KEYS.email_notifications_enabled]?.value !==
          "false",
      };
      reset(formValues);
    }
  }, [data, settingsMap, reset]);

  const onSubmit = async (formData: SettingsFormType) => {
    // Build batch of updates
    const updates: { uuid: string; value: string }[] = [];

    for (const [key, value] of Object.entries(formData)) {
      const setting = settingsMap[key];
      // Convert boolean to string for API
      const stringValue =
        typeof value === "boolean" ? String(value) : (value ?? "");
      if (setting?.uuid && setting.value !== stringValue) {
        updates.push({ uuid: setting.uuid, value: stringValue });
      }
    }

    if (updates.length === 0) {
      return;
    }

    const result = await updateSettings(updates);

    mutationFeedback(result, {
      success: t("admin.settings.messages.saved"),
      error: t("admin.settings.messages.saveError"),
    });
  };

  if (error) {
    return (
      <Box>
        <Typography color="error">{t("admin.common.loadingError")}</Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box>
        <Typography variant="h4" fontWeight={600} color="text.primary" mb={3}>
          {t("admin.settings.title")}
        </Typography>
        <Stack spacing={3}>
          {[1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={200}
              sx={{ borderRadius: 1 }}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      {/* Header */}
      <Typography variant="h4" fontWeight={600} color="text.primary" mb={3}>
        {t("admin.settings.title")}
      </Typography>

      <Stack spacing={3}>
        {/* Section: Contact Information */}
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={1}>
              {t("admin.settings.sections.contact")}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {t("admin.settings.sections.contactHelp")}
            </Typography>

            <Stack spacing={3}>
              {/* Residence Name */}
              <Controller
                name="residence_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("admin.settings.fields.residenceName")}
                    required
                    error={Boolean(errors.residence_name)}
                    helperText={errors.residence_name?.message}
                    fullWidth
                  />
                )}
              />

              {/* Address Line 1 */}
              <Controller
                name="address_line1"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("admin.settings.fields.addressLine1")}
                    required
                    error={Boolean(errors.address_line1)}
                    helperText={errors.address_line1?.message}
                    fullWidth
                  />
                )}
              />

              {/* Address Line 2 */}
              <Controller
                name="address_line2"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("admin.settings.fields.addressLine2")}
                    error={Boolean(errors.address_line2)}
                    helperText={errors.address_line2?.message}
                    fullWidth
                  />
                )}
              />

              {/* Phone + Email */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t("admin.settings.fields.phone")}
                      error={Boolean(errors.phone)}
                      helperText={
                        errors.phone?.message ??
                        t("admin.settings.fields.phoneHelper")
                      }
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t("admin.settings.fields.emailPublic")}
                      type="email"
                      required
                      error={Boolean(errors.email)}
                      helperText={errors.email?.message}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Section: Notifications */}
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={1}>
              {t("admin.settings.sections.notifications")}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {t("admin.settings.sections.notificationsHelp")}
            </Typography>

            <Controller
              name="email_notification"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("admin.settings.fields.emailNotification")}
                  type="email"
                  required
                  error={Boolean(errors.email_notification)}
                  helperText={errors.email_notification?.message}
                  fullWidth
                />
              )}
            />

            <Controller
              name="email_notifications_enabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={t("admin.settings.fields.emailNotificationsEnabled")}
                  sx={{ color: "text.primary", mt: 2 }}
                />
              )}
            />
          </CardContent>
        </Card>

        {/* Section: Opening Hours */}
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={1}>
              {t("admin.settings.sections.hours")}
            </Typography>

            <Controller
              name="opening_hours"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("admin.settings.fields.openingHours")}
                  multiline
                  rows={4}
                  error={Boolean(errors.opening_hours)}
                  helperText={errors.opening_hours?.message}
                  fullWidth
                />
              )}
            />
          </CardContent>
        </Card>

        {/* Save Button */}
        <Box display="flex" justifyContent="flex-end">
          <LoadingButton
            type="submit"
            variant="contained"
            size="large"
            loading={isUpdating}
            disabled={!isDirty}
          >
            {t("admin.common.save")}
          </LoadingButton>
        </Box>
      </Stack>
    </Box>
  );
}
